import { SelectionModel } from '@angular/cdk/collections';
import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { MatTreeModule, MatTreeNestedDataSource } from '@angular/material/tree';
import { FieldType, FormlyModule } from '@ngx-formly/core';
import { isObservable } from 'rxjs';
import { TreeNode } from '../../gk-map/models';

@Component({
  selector: 'gk-formly-mat-tree-select-type',
  imports: [
    CommonModule,
    MatInputModule,
    MatCardModule,
    MatTreeModule,
    MatCheckbox,
    ReactiveFormsModule,
    FormlyModule,
  ],
  template: `
    <input
      matInput
      [placeholder]="props.placeholder"
      [attr.id]="id"
      [value]="getSelectedNames()"
      readonly
      [ngClass]="[
        props?.attributes?.['class'] ? props.attributes['class'] : '',
        props.required ? 'required' : '',
      ]"
      (focus)="openMenu()"
      (click)="openMenu()"
    />

    <ng-template #treeMenu>
      <mat-card
        class="tree-overlay position-absolute w-100 bg-white overflow-auto p-2"
      >
        <mat-tree
          [dataSource]="dataSource"
          [childrenAccessor]="childrenAccessor"
        >
          <mat-tree-node
            class="tree-node-row"
            *matTreeNodeDef="let node"
            matTreeNodeToggle
          >
            <mat-checkbox
              [checked]="selection.isSelected(node)"
              (change)="toggleNode(node)"
            >
              {{ node.name }}
            </mat-checkbox>
          </mat-tree-node>
          <mat-nested-tree-node *matTreeNodeDef="let node; when: hasChild">
            <div>
              <div class="tree-node-row">
                <mat-checkbox
                  [checked]="areAllChildrenSelected(node)"
                  [indeterminate]="descendantsPartiallySelected(node)"
                  (change)="toggleNode(node)"
                >
                  {{ node.name }}
                </mat-checkbox>
              </div>
              <div class="ms-3">
                <ng-container matTreeNodeOutlet></ng-container>
              </div>
            </div>
          </mat-nested-tree-node>
        </mat-tree>
      </mat-card>
    </ng-template>

    <ng-container *ngIf="menuOpen">
      <div
        class="backdrop position-fixed top-0 bottom-0 start-0 end-0"
        (click)="closeMenu()"
      ></div>
      <ng-container *ngTemplateOutlet="treeMenu"></ng-container>
    </ng-container>
  `,
  styles: [
    `
      .backdrop {
        z-index: 999;
      }

      .tree-overlay {
        z-index: 1000;
        max-height: 400px;
        border: 1px solid #ccc;
      }

      .tree-node-row:hover {
        background-color: #f5f5f5;
      }
    `,
  ],
})
export class FormlyMatTreeSelectTypeComponent
  extends FieldType
  implements OnInit
{
  childrenAccessor = (node: TreeNode): TreeNode[] => node.children;
  dataSource = new MatTreeNestedDataSource<TreeNode>();
  selection = new SelectionModel<TreeNode>(true);
  menuOpen = false;

  ngOnInit(): void {
    const options = this.props.options || [];
    if (isObservable(options)) {
      options.subscribe((data: TreeNode[]) => {
        this.dataSource.data = data;
      });
    } else {
      this.dataSource.data = options;
    }

    this.formControl.valueChanges.subscribe((ids: string[]) => {
      if (ids && ids.length > 0) {
        this.restoreSelection(ids);
      } else {
        this.selection.clear();
      }
    });
  }

  hasChild = (_: number, node: TreeNode): boolean =>
    !!node.children && node.children.length > 0;

  toggleNode(node: TreeNode): void {
    if (node.id === null && node.children && node.children.length) {
      const allChildren = this.getAllLeafChildren(node);
      const allSelected = allChildren.every((child) =>
        this.selection.isSelected(child),
      );
      if (allSelected) {
        allChildren.forEach((child) => this.selection.deselect(child));
      } else {
        allChildren.forEach((child) => this.selection.select(child));
      }
    } else {
      this.selection.toggle(node);
    }

    const selectedLeafIds = this.selection.selected
      .filter((n) => n.id !== null)
      .map((n) => n.id);
    this.formControl.setValue(selectedLeafIds);
  }

  getAllLeafChildren(node: TreeNode): TreeNode[] {
    if (!node.children || node.children.length === 0) {
      return node.id !== null ? [node] : [];
    }
    return node.children.flatMap((child) => this.getAllLeafChildren(child));
  }

  descendantsPartiallySelected(node: TreeNode): boolean {
    const descendants = this.selection.selected.filter(
      (n) => n.id !== node.id && this.isDescendant(n, node),
    );
    return descendants.length > 0 && descendants.length < node.children?.length;
  }

  isDescendant(child: TreeNode, parent: TreeNode): boolean {
    if (!parent.children) return false;
    return parent.children.some(
      (n) => n.id === child.id || this.isDescendant(child, n),
    );
  }

  getSelectedNames(): string {
    return this.selection.selected
      .filter((n) => n.id !== null)
      .map((n) => n.name)
      .join(', ');
  }

  openMenu(): void {
    this.menuOpen = true;
  }

  closeMenu(): void {
    this.menuOpen = false;
  }

  restoreSelection(ids: string[]): void {
    const flatten = (nodes: TreeNode[]): TreeNode[] =>
      nodes.reduce(
        (acc, node) =>
          acc.concat([node], node.children ? flatten(node.children) : []),
        [],
      );
    const allNodes = flatten(this.dataSource.data);
    ids.forEach((id) => {
      const node = allNodes.find((n) => n.id === id);
      if (node) this.selection.select(node);
    });
  }

  areAllChildrenSelected(node: TreeNode): boolean {
    const children = this.getAllLeafChildren(node);
    return (
      children.length > 0 &&
      children.every((child) => this.selection.isSelected(child))
    );
  }
}
